/**
 * OccasionsSection
 *
 * "Every occasion, covered." A visual list of every occasion type MomentSent
 * supports, so visitors can see the full scope before signing up. Mirrors the
 * exact occasion set in the product (OccasionForm) so the marketing promise and
 * the actual app stay in sync.
 */

const OCCASIONS = [
  { emoji: '🎂', label: 'Birthday',        blurb: 'Their big day, every single year' },
  { emoji: '💍', label: 'Anniversary',     blurb: 'Wedding, dating, or any milestone' },
  { emoji: '🎄', label: 'Christmas',       blurb: 'The whole list, handled for you' },
  { emoji: '💝', label: "Valentine's Day", blurb: 'For the people you love most' },
  { emoji: '🌸', label: "Mother's Day",    blurb: 'Never the one who forgets again' },
  { emoji: '👔', label: "Father's Day",    blurb: 'A real card he can hold' },
  { emoji: '🎓', label: 'Graduation',      blurb: 'Celebrate the big achievement' },
  { emoji: '💌', label: 'Thinking of You', blurb: 'Just because, on any date' },
  { emoji: '✨', label: 'Custom occasion',  blurb: 'Name your own and we send it' },
] as const;

export default function OccasionsSection() {
  return (
    <section id="occasions" className="bg-cream py-24">
      <div className="section-container">

        {/* Heading */}
        <div className="text-center mb-16">
          <p className="font-body text-xs font-semibold tracking-widest text-sage uppercase mb-3">
            Every occasion, covered
          </p>
          <h2 className="section-heading text-4xl sm:text-5xl">
            One service.<br className="hidden sm:block" /> Every moment that matters.
          </h2>
          <p className="mt-4 font-body text-gray-500 text-lg max-w-xl mx-auto leading-relaxed">
            Set up the people you care about once. MomentSent sends a real card for
            every occasion below, recurring year after year or as a one time send.
          </p>
        </div>

        {/* Occasion grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6">
          {OCCASIONS.map((o) => (
            <div
              key={o.label}
              className="card flex items-center gap-4 p-5 group hover:shadow-warm-lg transition-shadow duration-300"
            >
              <div className="text-3xl sm:text-4xl flex-shrink-0 leading-none">{o.emoji}</div>
              <div>
                <h3 className="font-heading text-base sm:text-lg text-navy leading-tight">{o.label}</h3>
                <p className="font-body text-xs sm:text-sm text-gray-500 leading-snug mt-0.5">{o.blurb}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom note */}
        <div className="mt-14 text-center">
          <div className="inline-flex items-center gap-2 bg-white rounded-full px-6 py-3 shadow-warm">
            <span className="text-lg">🗓️</span>
            <p className="font-body text-sm text-navy">
              <span className="font-semibold">Recurring occasions repeat automatically.</span>
              {' '}Birthdays and anniversaries send every year without you lifting a finger.
            </p>
          </div>
        </div>

      </div>
    </section>
  );
}
